import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/useAuth';
import { useApi } from '../lib/api';
import { Link } from 'react-router-dom';
import { hoursToDays, cn } from '../lib/utils';
import {
    Clock,
    Calendar,
    FileCheck,
    AlertCircle,
    ArrowRight,
    TrendingUp,
    Users,
    FolderKanban,
    Hourglass,
    TrendingDown,
    CalendarX,
    Building2,
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { ApiResponse, UserStats, GlobalStats, ProjectsHealth } from '../types';

export function Dashboard() {
    const { user, isManager, isAdmin, isAuthenticated, isLoading } = useAuth();
    const api = useApi();

    // Fetch user stats - only when authenticated
    const { data: userStats } = useQuery({
        queryKey: ['stats', 'me'],
        queryFn: () => api.get<ApiResponse<UserStats>>('/api/stats/me'),
        enabled: isAuthenticated && !isLoading,
    });

    // Fetch global stats (for managers/admins)
    const { data: globalStats } = useQuery({
        queryKey: ['stats', 'global'],
        queryFn: () => api.get<ApiResponse<GlobalStats>>('/api/stats'),
        enabled: (isManager || isAdmin) && isAuthenticated && !isLoading,
    });

    // Projets internes masqués par défaut dans la santé des projets : un projet interne
    // n'a pas de reste-à-faire à surveiller. Le critère vient du client marqué interne dans
    // l'administration, ou d'un projet dont le type de facturation vaut « internal ».
    const [includeInternal, setIncludeInternal] = useState(false);

    // Fetch projects health (admin only)
    const { data: projectsHealth } = useQuery({
        queryKey: ['stats', 'projects-health', { includeInternal }],
        queryFn: () =>
            api.get<ApiResponse<ProjectsHealth>>(
                `/api/stats/projects-health${includeInternal ? '?includeInternal=true' : ''}`
            ),
        enabled: isAdmin && isAuthenticated && !isLoading,
    });

    const stats = userStats?.data;
    const global = globalStats?.data;
    const health = projectsHealth?.data;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--color-text)]">
                        Bonjour, {user?.firstName} 👋
                    </h1>
                    <p className="text-[var(--color-text-secondary)]">
                        {format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}
                    </p>
                </div>
                <Link
                    to="/time-entry"
                    className="btn-primary"
                >
                    <Clock className="w-4 h-4" />
                    Saisir du temps
                </Link>
            </div>

            {/* User Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title="Jours cette semaine"
                    value={`${hoursToDays(stats?.hoursThisWeek ?? 0)}j`}
                    icon={Clock}
                    color="blue"
                />
                <StatCard
                    title="Jours ce mois"
                    value={`${hoursToDays(stats?.hoursThisMonth ?? 0)}j`}
                    icon={Calendar}
                    color="green"
                />
                <StatCard
                    title="Brouillons"
                    value={stats?.draftEntries ?? 0}
                    icon={FileCheck}
                    color="yellow"
                    link="/time-entry"
                />
                <StatCard
                    title="À corriger"
                    value={stats?.rejectedEntries ?? 0}
                    icon={AlertCircle}
                    color="red"
                    link="/time-entry?status=rejected"
                />
            </div>

            {/* Alerts */}
            {stats && (stats.missingDaysThisWeek > 0 || stats.rejectedEntries > 0) && (
                <div className="bg-[var(--color-warning-light)] border border-[var(--color-warning)] rounded-lg p-4">
                    <h3 className="font-medium text-[#b45309] mb-2">
                        Actions requises
                    </h3>
                    <ul className="space-y-1 text-sm text-[#b45309]">
                        {stats.missingDaysThisWeek > 0 && (
                            <li className="flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" />
                                {stats.missingDaysThisWeek} jour(s) sans saisie cette semaine
                            </li>
                        )}
                        {stats.rejectedEntries > 0 && (
                            <li className="flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" />
                                {stats.rejectedEntries} saisie(s) rejetée(s) à corriger
                            </li>
                        )}
                    </ul>
                </div>
            )}

            {/* Manager/Admin Stats */}
            {(isManager || isAdmin) && global && (
                <div className="mt-8">
                    <h2 className="text-lg font-semibold text-[var(--color-text)] mb-4">
                        Vue d'ensemble
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard
                            title="Utilisateurs actifs"
                            value={global.activeUsers}
                            icon={Users}
                            color="blue"
                        />
                        <StatCard
                            title="Projets actifs"
                            value={global.activeProjects}
                            icon={FolderKanban}
                            color="green"
                        />
                        <StatCard
                            title="Validations en attente"
                            value={global.pendingApprovals}
                            icon={FileCheck}
                            color="yellow"
                            link="/approvals"
                        />
                        <StatCard
                            title="Jours ce mois"
                            value={`${hoursToDays(global.hoursThisMonth)}j`}
                            icon={TrendingUp}
                            color="purple"
                        />
                    </div>
                </div>
            )}

            {/* Admin: santé des projets (reste-à-faire + projets en défaut) */}
            {isAdmin && health && (
                <div className="mt-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                        <h2 className="text-lg font-semibold text-[var(--color-text)]">
                            Santé des projets
                        </h2>
                        <button
                            onClick={() => setIncludeInternal((v) => !v)}
                            className={cn(
                                'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-sm self-start sm:self-auto',
                                includeInternal
                                    ? 'bg-[var(--color-bg-hover)] border-[var(--color-border)] text-[var(--color-text)]'
                                    : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]'
                            )}
                            title="Projets d'un client marqué interne, ou dont le type de facturation vaut « internal »"
                        >
                            <Building2 className="w-4 h-4" />
                            {includeInternal ? 'Masquer les projets internes' : 'Inclure les projets internes'}
                        </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <StatCard
                            title="Jours restant à effectuer"
                            value={`${hoursToDays(health.totalRemainingHours)}j`}
                            icon={Hourglass}
                            color="blue"
                        />
                        <StatCard
                            title="Projets en dépassement"
                            value={health.overrunCount}
                            icon={TrendingDown}
                            color="red"
                        />
                        <StatCard
                            title="Projets échus à finir"
                            value={health.overdueCount}
                            icon={CalendarX}
                            color="yellow"
                        />
                    </div>

                    {/* Détail : projets en dépassement */}
                    {health.overrunProjects.length > 0 && (
                        <div className="card mt-4">
                            <h3 className="font-medium text-[var(--color-text)] mb-3 flex items-center gap-2">
                                <TrendingDown className="w-4 h-4 text-red-600" />
                                Projets en dépassement de budget
                            </h3>
                            <ul className="divide-y divide-[var(--color-border)]">
                                {health.overrunProjects.map((p) => (
                                    <li key={p.id}>
                                        <Link
                                            to={`/projects/${p.id}`}
                                            className="flex items-center justify-between py-2 hover:text-[var(--color-primary)] transition-colors"
                                        >
                                            <span className="min-w-0 truncate">
                                                <span className="font-medium">{p.code}</span>
                                                <span className="text-[var(--color-text-secondary)]"> · {p.name}</span>
                                                {p.clientName && (
                                                    <span className="text-[var(--color-text-muted)]"> — {p.clientName}</span>
                                                )}
                                            </span>
                                            <span className="ml-4 flex-shrink-0 text-sm font-semibold text-red-600">
                                                +{hoursToDays(p.overrunHours)}j
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Détail : projets échus avec reste-à-faire */}
                    {health.overdueProjects.length > 0 && (
                        <div className="card mt-4">
                            <h3 className="font-medium text-[var(--color-text)] mb-3 flex items-center gap-2">
                                <CalendarX className="w-4 h-4 text-yellow-600" />
                                Projets échus avec reste à faire
                            </h3>
                            <ul className="divide-y divide-[var(--color-border)]">
                                {health.overdueProjects.map((p) => (
                                    <li key={p.id}>
                                        <Link
                                            to={`/projects/${p.id}`}
                                            className="flex items-center justify-between py-2 hover:text-[var(--color-primary)] transition-colors"
                                        >
                                            <span className="min-w-0 truncate">
                                                <span className="font-medium">{p.code}</span>
                                                <span className="text-[var(--color-text-secondary)]"> · {p.name}</span>
                                                <span className="text-[var(--color-text-muted)]">
                                                    {' '}— échéance {format(new Date(p.endDate), 'd MMM yyyy', { locale: fr })}
                                                </span>
                                            </span>
                                            <span className="ml-4 flex-shrink-0 text-sm font-semibold text-yellow-600">
                                                {hoursToDays(p.remainingHours)}j restants
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <QuickActionCard
                    title="Saisie rapide"
                    description="Ajouter une entrée pour aujourd'hui"
                    icon={Clock}
                    href="/time-entry"
                />
                <QuickActionCard
                    title="Vue semaine"
                    description="Voir et compléter la semaine en cours"
                    icon={Calendar}
                    href="/week"
                />
                <QuickActionCard
                    title="Mes projets"
                    description="Consulter les projets et leur avancement"
                    icon={FolderKanban}
                    href="/projects"
                />
            </div>
        </div>
    );
}

interface StatCardProps {
    title: string;
    value: string | number;
    icon: React.ComponentType<{ className?: string }>;
    color: 'blue' | 'green' | 'yellow' | 'red' | 'purple';
    link?: string;
}

function StatCard({ title, value, icon: Icon, color, link }: StatCardProps) {
    const colorClasses = {
        blue: 'bg-blue-50 text-blue-600',
        green: 'bg-green-50 text-green-600',
        yellow: 'bg-yellow-50 text-yellow-600',
        red: 'bg-red-50 text-red-600',
        purple: 'bg-purple-50 text-purple-600',
    };

    const content = (
        <div className="card hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm text-[var(--color-text-secondary)]">{title}</p>
                    <p className="text-2xl font-bold text-[var(--color-text)] mt-1">{value}</p>
                </div>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
                    <Icon className="w-6 h-6" />
                </div>
            </div>
        </div>
    );

    if (link) {
        return <Link to={link}>{content}</Link>;
    }

    return content;
}

interface QuickActionCardProps {
    title: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    href: string;
}

function QuickActionCard({ title, description, icon: Icon, href }: QuickActionCardProps) {
    return (
        <Link
            to={href}
            className="card hover:shadow-md transition-shadow group"
        >
            <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[var(--color-primary-light)] flex items-center justify-center text-[var(--color-primary)]">
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-[var(--color-text)] group-hover:text-[var(--color-primary)] transition-colors">
                        {title}
                    </h3>
                    <p className="text-sm text-[var(--color-text-secondary)] mt-1">{description}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-[var(--color-text-muted)] group-hover:text-[var(--color-primary)] transition-colors flex-shrink-0" />
            </div>
        </Link>
    );
}
